import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';
import { CalibrationCustomer } from './apiDtos/calibrationCustomer';
import { CalibrationCertificate } from './apiDtos/CalibrationCertificate';
import { CoreConfigService } from '@core/services/config.service';
import { CoreConfig } from '@core/types';

@Injectable({
  providedIn: 'root'
})
export class TurkakService {
  private apiUrl: string;
  private coreConfig: CoreConfig;

  constructor(
    private httpClient: HttpClient,
    @Inject('baseurl') private baseurl: string,
    private coreConfigService: CoreConfigService
  ) {
    this.apiUrl = this.baseurl + '/Turkaks';

    this.coreConfigService.getConfig().subscribe(config => {
      this.coreConfig = config;
    });
  }

  private get token(): string | null {
    return localStorage.getItem('turkakToken');
  }

  private get isAuthorized(): boolean {
    return this.coreConfig?.isCalibratedTurkak && !!this.token;
  }

  async getCustomers(): Promise<any> {
    if (!this.isAuthorized) return null;

    const headers = new HttpHeaders({ token: this.token! });
    return await firstValueFrom(
      this.httpClient.get(`${this.apiUrl}/customers`, { headers })
    );
  }

  async saveCustomers(customers: CalibrationCustomer[]): Promise<any> {
    if (!this.isAuthorized) return null;

    const headers = new HttpHeaders({ token: this.token! });
    return await firstValueFrom(
      this.httpClient.post(`${this.apiUrl}/customers`, customers, { headers })
    );
  }

  async saveCertificates(certificates: CalibrationCertificate[]): Promise<any> {
    if (!this.isAuthorized) return null;

    const headers = new HttpHeaders({ token: this.token! });
    return await firstValueFrom(
      this.httpClient.post(`${this.apiUrl}/certificates`, certificates, { headers })
    );
  }

  async getCertificate(certificateId: string, responseType: 'json' | 'arraybuffer' | 'blob' = 'json'): Promise<any> {
    if (!this.isAuthorized) return null;

    const headers = new HttpHeaders({ token: this.token! });
    return await firstValueFrom(
      this.httpClient.get(`${this.apiUrl}/certificates/${certificateId}`
        , { headers, responseType: responseType as any }),

    );
  }

  async getCertificateQrPng(certID: string, tbdsNumber: string): Promise<Blob | null> {
    if (!this.isAuthorized) return null;

    const url = `${this.apiUrl}/qr/png?certID=${certID}&tbdsNumber=${tbdsNumber}`;
    return await firstValueFrom(
      this.httpClient.get(url, { responseType: 'blob' })
    );
  }

  async getCertificateQrHtmlLink(certID: string, tbdsNumber: string): Promise<{ link: string } | null> {
    if (!this.isAuthorized) return null;

    const url = `${this.apiUrl}/qr/html-link?certID=${certID}&tbdsNumber=${tbdsNumber}`;
    return await firstValueFrom(
      this.httpClient.get<{ link: string }>(url)
    );
  }

  // Aşağıdaki kodlar eski doğrudan Turkak API'sine erişen versiyondur:

  /*
  private apiUrl = 'https://api.turkak.org.tr/';
  coreConfig: CoreConfig;

  constructor(
    private httpClient: HttpClient,
    private coreConfigService: CoreConfigService
  ) {
    this.coreConfigService.getConfig().subscribe(config => {
      this.coreConfig = config;
    });
  }

  async login(username, password) {
    if (!this.coreConfig.isCalibratedTurkak) return Promise.resolve(null);
    const observable: Observable<any> = this.httpClient.post(this.apiUrl + 'SSO/signin', {
      Password: password,
      Username: username
    });
    return await firstValueFrom(observable);
  }

  async calibrationCustomerGetData() {
    if (!this.coreConfig.isCalibratedTurkak) return Promise.resolve(null);
    const token = localStorage.getItem("turkakToken");
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });
    const observable: Observable<any> = this.httpClient.get(this.apiUrl + "TBDS/api/v1/CalibrationService/CalibrationCustomerGetData/tr", { headers });
    return await firstValueFrom(observable);
  }

  async calibrationCustomerSaveData(calibrationCustomer: CalibrationCustomer[]) {
    if (!this.coreConfig.isCalibratedTurkak) return Promise.resolve(null);
    const token = localStorage.getItem("turkakToken");
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    });
    const observable: Observable<any> = this.httpClient.post(this.apiUrl + "TBDS/api/v1/CalibrationService/CalibrationCustomerSaveData/", calibrationCustomer, { headers });
    return await firstValueFrom(observable);
  }

  async calibrationCertificateSaveData(calibrationCertificates: CalibrationCertificate[]) {
    if (!this.coreConfig.isCalibratedTurkak) return Promise.resolve(null);
    const token = localStorage.getItem("turkakToken");
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    });

    const observable: Observable<any> = this.httpClient.post(
      this.apiUrl + "TBDS/api/v1/CalibrationService/CalibrationCertificateSaveData/",
      calibrationCertificates,
      { headers }
    );

    return await firstValueFrom(observable);
  }

  async CalibrationCertificateGetCertificate(CertificateId) {
    if (!this.coreConfig.isCalibratedTurkak) return Promise.resolve(null);
    const token = localStorage.getItem("turkakToken");
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    });
    const url = `${this.apiUrl}TBDS/api/v1/CalibrationService/CalibrationCertificateGetCertificate/${CertificateId}`;
    const observable: Observable<any> = this.httpClient.get(
      url,
      { headers },
    );

    return await firstValueFrom(observable);
  }

  getCertificateQR(certID: string, tbdsNumber: string): Promise<Blob> {
    if (!this.coreConfig.isCalibratedTurkak) return Promise.resolve(null);
    const url = `https://api.turkak.org.tr/TBDS/api/v1/DocumentVerification/GetQR/${certID}/${tbdsNumber}`;
    return this.httpClient.get(url, { responseType: 'blob' }).toPromise();
  }
  */
}
